import { toast } from 'sonner';

interface UploadTracker {
  startTime: number;
  fileSize: number;
  fileName: string;
  toastId20s?: string | number;
  toastId40s?: string | number;
  hasShown20s: boolean;
  hasShown40s: boolean;
}

const uploadTrackers = new Map<string, UploadTracker>();

/**
 * Format bytes to human-readable size
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format seconds to human-readable time
 */
function formatTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} seconds`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (remainingSeconds === 0) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Calculate estimated time remaining based on upload speed
 */
function calculateETA(bytesUploaded: number, bytesTotal: number, elapsedSeconds: number): string {
  if (bytesUploaded === 0 || elapsedSeconds === 0) return 'calculating...';

  const bytesRemaining = bytesTotal - bytesUploaded;
  const uploadSpeed = bytesUploaded / elapsedSeconds; // bytes per second
  const secondsRemaining = bytesRemaining / uploadSpeed;

  return formatTime(secondsRemaining);
}

/**
 * Start tracking an upload
 */
export function startUploadTracking(uploadId: string, fileSize: number, fileName: string): void {
  uploadTrackers.set(uploadId, {
    startTime: Date.now(),
    fileSize,
    fileName,
    hasShown20s: false,
    hasShown40s: false,
  });
}

/**
 * Update upload progress and show notifications at thresholds
 */
export function trackUploadProgress(
  uploadId: string,
  bytesUploaded: number,
  bytesTotal: number
): void {
  const tracker = uploadTrackers.get(uploadId);
  if (!tracker) return;

  const elapsedSeconds = (Date.now() - tracker.startTime) / 1000;
  const percentage = Math.round((bytesUploaded / bytesTotal) * 100);

  // Show notification at 20-second threshold
  if (elapsedSeconds >= 20 && !tracker.hasShown20s) {
    tracker.hasShown20s = true;
    tracker.toastId20s = toast.info(
      `Still uploading ${tracker.fileName}...`,
      {
        description: `${formatFileSize(tracker.fileSize)} file - ${percentage}% complete. This is taking longer than usual.`,
        duration: 10000, // Show for 10 seconds
      }
    );
  }

  // Show notification at 40-second threshold with ETA
  if (elapsedSeconds >= 40 && !tracker.hasShown40s) {
    tracker.hasShown40s = true;
    const eta = calculateETA(bytesUploaded, bytesTotal, elapsedSeconds);

    tracker.toastId40s = toast.info(
      `Large file upload in progress`,
      {
        description: `${formatFileSize(tracker.fileSize)} - ${percentage}% complete. Estimated time remaining: ${eta}`,
        duration: 15000, // Show for 15 seconds
      }
    );
  }
}

/**
 * Complete upload tracking and dismiss any active toasts
 */
export function completeUploadTracking(uploadId: string): void {
  const tracker = uploadTrackers.get(uploadId);
  if (!tracker) return;

  // Dismiss any active notification toasts
  if (tracker.toastId20s) {
    toast.dismiss(tracker.toastId20s);
  }
  if (tracker.toastId40s) {
    toast.dismiss(tracker.toastId40s);
  }

  // Clean up tracker
  uploadTrackers.delete(uploadId);
}

/**
 * Cancel upload tracking (e.g., if upload fails or is cancelled)
 */
export function cancelUploadTracking(uploadId: string): void {
  completeUploadTracking(uploadId);
}
